<template>
  <transition name="scroll-button">
    <button
      v-if="showButton"
      class="scroll-to-bottom-button"
      @click="handleClick"
      title="Scroll to bottom"
    >
      <i class="fas fa-chevron-down"></i>
    </button>
  </transition>
</template>

<script>
export default {
  name: "ScrollToBottomButton",
  props: {
    /**
     * The scrollable container to monitor.
     * If null, defaults to window.
     * Can be a direct HTMLElement or a Vue ref (e.g., this.$refs.container).
     */
    container: {
      type: [Object, HTMLElement],
      required: false,
      default: null,
    },

    /**
     * Whether to use smooth scrolling when scrolling to bottom.
     */
    smooth: {
      type: Boolean,
      default: true,
    },

    /**
     * Distance from bottom (in pixels) to trigger showing the button.
     */
    scrollDistanceThreshold: {
      type: Number,
      default: 200,
    },
  },
  data() {
    return {
      showButton: false,
    };
  },
  watch: {
    container: {
      immediate: true,
      handler(newContainer, oldContainer) {
        if (oldContainer) {
          this.detachScrollListener();
        }
        if (newContainer) {
          this.$nextTick(() => {
            this.attachScrollListener();
          });
        }
      },
    },
  },
  beforeDestroy() {
    this.detachScrollListener();
  },
  methods: {
    attachScrollListener() {
      const container = this.getContainer();
      if (container) {
        container.addEventListener("scroll", this.handleScroll);
      }
    },

    detachScrollListener() {
      const container = this.getContainer();
      if (container) {
        container.removeEventListener("scroll", this.handleScroll);
      }
    },

    getContainer() {
      return this.container?.$el || this.container;
    },

    handleScroll() {
      const container = this.getContainer();
      if (!container) return;

      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

      const isScrollable = scrollHeight > clientHeight;

      // Show button when scrolled up beyond threshold
      this.showButton =
        distanceFromBottom > this.scrollDistanceThreshold && isScrollable;
    },

    handleClick() {
      const container = this.getContainer();
      if (container) {
        if (this.smooth) {
          container.scrollTo({
            top: container.scrollHeight,
            behavior: "smooth",
          });
        } else {
          container.scrollTop = container.scrollHeight;
        }
      }
      this.$emit("click");
    },
  },
};
</script>

<style lang="scss" scoped>
.scroll-to-bottom-button {
  position: absolute;
  bottom: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: white;
  border: 1px solid var(--oliver-accent-border, rgba(143, 138, 230, 0.3));
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: all 0.2s ease;

  i {
    font-size: 16px;
    color: var(--oliver-accent, #8f8ae6);
  }

  &:hover {
    background: var(--oliver-accent, #8f8ae6);
    border-color: var(--oliver-accent, #8f8ae6);
    transform: translateY(-2px);
    box-shadow: 0 6px 16px var(--oliver-accent-border, rgba(143, 138, 230, 0.3));

    i {
      color: white;
    }
  }

  &:active {
    transform: translateY(0);
  }
}

.scroll-button-enter-active,
.scroll-button-leave-active {
  transition: all 0.3s ease;
}

.scroll-button-enter,
.scroll-button-leave-to {
  opacity: 0;
  transform: translateY(10px) scale(0.8);
}

@media (max-width: 768px) {
  .scroll-to-bottom-button {
    bottom: 16px;
    right: 16px;
    width: 44px;
    height: 44px;

    i {
      font-size: 18px;
    }
  }
}
</style>
